/**
 * Funnel Events - Strict acquisition-to-activation tracking
 * 
 * Tracks the user journey through defined funnel stages:
 * 1. Entry (landing, referral source)
 * 2. Engagement (search, browse, entity view)
 * 3. Signup Intent (form start, field interaction)
 * 4. Registration (form submit, account created)
 * 5. Activation (first action: sighting, favorite, message)
 * 
 * @idempotent - Events include deduplication checks to prevent double-firing
 * @module
 */

import { trackEvent } from './core';
import { createLogger } from '../logger';

const logger = createLogger('FunnelEvents');

// Session-level flags to prevent duplicate events
const sessionFlags = {
  landingTracked: false,
  signupFormStarted: false,
  firstEntityViewed: false,
  firstSearchPerformed: false,
};

/**
 * Track traffic source and landing page
 * Called once per session on app initialization
 */
export const trackLanding = (source) => {
  if (!source) return
  if (sessionFlags.landingTracked) {
    logger.debug('Landing already tracked in this session');
    return;
  }

  const params = {
    landing_page: window.location.pathname,
    referrer: document.referrer || 'direct',
    utm_source: source.utm_source || 'direct',
    utm_medium: source.utm_medium || 'none',
    utm_campaign: source.utm_campaign || null,
    utm_term: source.utm_term || null,
    utm_content: source.utm_content || null,
    entry_timestamp: new Date().toISOString(),
  };

  trackEvent('Funnel', 'landing', 'page_entry', null, params);
  sessionFlags.landingTracked = true;
  logger.info('Landing tracked', params);
};

/**
 * Track signup form start
 * Fires when user first interacts with signup form
 */
export const trackSignupFormStart = () => {
  if (sessionFlags.signupFormStarted) {
    logger.debug('Signup form start already tracked');
    return;
  }

  trackEvent('Funnel', 'signup_form_start', 'form_interaction', null, {
    form_type: 'registration',
    timestamp: new Date().toISOString(),
  });

  sessionFlags.signupFormStarted = true;
  logger.info('Signup form start tracked');
};

/**
 * Track field interaction events
 * Fires when user focuses on a form field
 */
export const trackFieldFocus = (fieldName) => {
  trackEvent('Funnel', 'field_focus', fieldName, null, {
    field_name: fieldName,
    form_type: 'registration',
  });
};

/**
 * Track field blur with validation state
 * Fires when user leaves a form field
 */
export const trackFieldBlur = (fieldName, isValid, errorMessage = null) => {
  trackEvent('Funnel', 'field_blur', fieldName, null, {
    field_name: fieldName,
    is_valid: isValid,
    error_message: errorMessage,
  });
};

/**
 * Track validation errors
 * Fires when form validation fails
 */
export const trackValidationError = (fieldName, errorMessage) => {
  trackEvent('Funnel', 'validation_error', fieldName, null, {
    field_name: fieldName,
    error_message: errorMessage,
    form_type: 'registration',
  });
};

/**
 * Track signup form submission
 * Fires when user clicks submit button
 */
export const trackSignupSubmit = (method = 'email') => {
  const formStartTime = sessionStorage.getItem('signup_form_start_time');
  const submissionTime = Date.now();
  const timeToComplete = formStartTime
    ? (submissionTime - parseInt(formStartTime)) / 1000
    : null;

  trackEvent('Funnel', 'signup_submit', method, null, {
    method,
    time_to_complete_seconds: timeToComplete,
    timestamp: new Date().toISOString(),
  });

  logger.info('Signup submit tracked', { method, timeToComplete });
};

/**
 * Track successful account creation
 * Fires immediately after backend confirms user creation
 */
export const trackAccountCreated = (userId, method = 'email') => {
  const formStartTime = sessionStorage.getItem('signup_form_start_time');
  const completionTime = Date.now();
  const totalTime = formStartTime
    ? (completionTime - parseInt(formStartTime)) / 1000
    : null;

  trackEvent('Funnel', 'account_created', method, null, {
    user_id: userId,
    method,
    total_signup_time_seconds: totalTime,
    timestamp: new Date().toISOString(),
  });

  // Clear form timing
  sessionStorage.removeItem('signup_form_start_time');

  logger.info('Account created tracked', { userId, method, totalTime });
};

/**
 * Track user activation milestones
 * Fires when user completes their first meaningful action
 */
export const trackActivation = (actionType, metadata = {}) => {
  // Check if this activation type was already tracked
  const flagKey = `activation_${actionType}_tracked`;
  if (localStorage.getItem(flagKey)) {
    logger.debug(`Activation ${actionType} already tracked`);
    return;
  }

  const accountCreatedTime = localStorage.getItem('account_created_at');
  const activationTime = Date.now();
  const timeToActivation = accountCreatedTime
    ? (activationTime - parseInt(accountCreatedTime)) / 1000
    : null;

  trackEvent('Funnel', 'user_activated', actionType, null, {
    activation_type: actionType,
    time_to_activation_seconds: timeToActivation,
    ...metadata,
    timestamp: new Date().toISOString(),
  });

  localStorage.setItem(flagKey, 'true');
  logger.info('Activation tracked', { actionType, timeToActivation, metadata });
};

/**
 * Track first entity view
 * Fires when user views their first entity detail
 */
export const trackFirstEntityView = (entityId, entityName) => {
  if (sessionFlags.firstEntityViewed) {
    return;
  }

  trackEvent('Funnel', 'first_entity_view', entityId, null, {
    entity_id: entityId,
    entity_name: entityName,
    timestamp: new Date().toISOString(),
  });

  sessionFlags.firstEntityViewed = true;
  logger.info('First entity view tracked', { entityId });
};

/**
 * Track first search
 * Fires when user performs their first search
 */
export const trackFirstSearch = (searchQuery) => {
  if (sessionFlags.firstSearchPerformed) {
    return;
  }

  trackEvent('Funnel', 'first_search', 'search_query', null, {
    search_query: searchQuery,
    timestamp: new Date().toISOString(),
  });

  sessionFlags.firstSearchPerformed = true;
  logger.info('First search tracked', { searchQuery });
};

/**
 * Initialize signup form timing
 * Called when signup form component mounts
 */
export const initSignupFormTiming = () => {
  if (!sessionStorage.getItem('signup_form_start_time')) {
    sessionStorage.setItem('signup_form_start_time', Date.now().toString());
  }
};

/**
 * Store account creation timestamp
 * Called immediately after successful registration
 */
export const storeAccountCreationTime = () => {
  if (!localStorage.getItem('account_created_at')) {
    localStorage.setItem('account_created_at', Date.now().toString());
  }
};

/**
 * Get traffic source from URL parameters
 * Extracts UTM parameters and referrer
 */
export const getTrafficSource = () => {
  const params = new URLSearchParams(window.location.search);
  
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
    referrer: document.referrer || null,
  };
};

/**
 * Store traffic source in session storage
 * Called once on app initialization
 */
export const storeTrafficSource = () => {
  if (sessionStorage.getItem('traffic_source')) {
    return; // Already stored
  }

  const source = getTrafficSource();
  sessionStorage.setItem('traffic_source', JSON.stringify(source));
  logger.info('Traffic source stored', source);
};

/**
 * Get stored traffic source
 * Returns parsed traffic source from session storage
 */
export const getStoredTrafficSource = () => {
  const stored = sessionStorage.getItem('traffic_source');
  if (!stored) return getTrafficSource();
  
  try {
    return JSON.parse(stored);
  } catch (error) {
    logger.error('Failed to parse stored traffic source', error);
    return getTrafficSource();
  }
};
